import React, { FC, useMemo } from 'react';
import { Text } from '@mofid-securities/uikit';
import { useFundEfficiencyCompareContext } from '../../contexts/fundEfficiencyCompareContext';
import {
  IInteractiveNavChartTooltipItem,
  InteractiveNavChart,
  InteractiveNavChartTooltip,
  TNavChartRenderTooltip,
} from '@/components/InteractiveNavChart';
import { useGetFundCodeFromQuery } from '@/modules/invest/hooks/useGetFundCodeFromQuery';
import { useGetFundDetails } from '@/modules/invest/subModules/fund/hooks/useGetFundDetails';
import { useGetHistoryComparisonAssetTrendData } from './HistoryComparisonAssetTrend.hooks';

const renderTooltip: TNavChartRenderTooltip = (chart) => {
  const tooltipItems: IInteractiveNavChartTooltipItem[] = chart?.points.map(
    (item: any) => {
      const tooltipItem: IInteractiveNavChartTooltipItem = {
        title: item?.series?.name,
        type: 'percentage',
        isZeroValueVisible: true,
        value: item?.y,
      };
      return tooltipItem;
    }
  );

  const interactiveNavChartTooltipItems: IInteractiveNavChartTooltipItem[] = [
    {
      title: 'تاریخ',
      value: chart.x,
      type: 'date',
    },
    ...tooltipItems,
  ];

  return <InteractiveNavChartTooltip items={interactiveNavChartTooltipItems} />;
};

const periods = {
  '1W': 'Return1W',
  '1M': 'Return1M',
  '3M': 'Return3M',
  '6M': 'Return6M',
  '1Y': 'Return1Y',
  '3Y': 'Return3Y',
  '5Y': 'Return5Y',
};

export const ComparisonNavChart: FC = () => {
  const fundCode = useGetFundCodeFromQuery();

  const { selectedParallelMarket, selectedPeriod } =
    useFundEfficiencyCompareContext();

  const { fundDetails } = useGetFundDetails(fundCode);

  const {
    fundHistoryRatio,
    parallelMarketHistory,
    errorMessage,
    isFetching,
    refetchOnError,
  } = useGetHistoryComparisonAssetTrendData();

  const series = useMemo(() => {
    const parallelDataHistory = parallelMarketHistory?.items?.find(
      (item) => item?.historyRatioType === (periods?.[selectedPeriod] as string)
    );

    const fundSymbolHistory = fundHistoryRatio?.items?.find(
      (item) => item?.historyRatioType === (periods?.[selectedPeriod] as string)
    );

    return [
      {
        name: selectedParallelMarket.titleFa || '',
        color: '#80D3CB',
        type: 'area',
        data: parallelDataHistory?.items.map((item) => [
          item?.time,
          item?.changePercent,
        ]),
      },
      {
        name: 'صندوق',
        color: fundDetails?.color || 'transparent',
        type: 'line',
        data: fundSymbolHistory?.items.map((item) => [
          item?.time,
          item?.changePercent,
        ]),
      },
    ];
  }, [selectedPeriod, isFetching, selectedParallelMarket]);

  const isChartEmpty = !series?.[1]?.data?.length;

  return (
    <>
      <Text
        className="text-gray-filled-secondary-enabled"
        component="h4"
        size="sm"
        weight="demibold"
      >
        مقایسه بازدهی {fundDetails?.shortName}
      </Text>
      <div className="my-2 flex justify-center gap-x-3 text-xs font-demibold">
        <div
          className="flex items-center"
          style={{
            color: fundDetails?.color,
          }}
        >
          <span
            className="ml-1 h-[4px] w-[14px]"
            style={{
              backgroundColor: fundDetails?.color,
            }}
          />
          {fundDetails?.shortName}
        </div>
        {selectedParallelMarket.titleFa && (
          <div className="flex items-center text-[#333333]">
            <span className="ml-1 h-[4px] w-[14px] bg-[#80D3CB]" />
            {selectedParallelMarket.titleFa}
          </div>
        )}
      </div>
      <InteractiveNavChart
        error={errorMessage}
        fillColor="#80D3CB"
        isEmpty={isChartEmpty}
        isLoading={isFetching}
        renderTooltip={renderTooltip}
        series={series}
        yAxisTitle="درصد"
        onErrorRetry={refetchOnError}
      />
    </>
  );
};
